/**
 * Copyright (c) HashiCorp, Inc.
 * SPDX-License-Identifier: BUSL-1.1
 */

import {
  HdsPageHeader,
  HdsTextBody,
  HdsLinkInline,
} from '@hashicorp/design-system-components/components';

import './secret-inventory.scss';
import Component from '@glimmer/component';
import { flattenFilters, type Filter } from '../../..//utils/filters.ts';
import InventoryTable, {
  type InventoryTableColumn,
  type QuickFilter,
} from '../inventory-table.gts';
import { type FilterFieldDefinition } from '../filter-bar.gts';
import updateData from '../../../modifiers/update-data.ts';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import type ReportingApiService from '../../../services/reporting-api.ts';
import type {
  VaultReportingServiceApiFetchSecretsInventoryCountRequest,
  VaultReportingServiceApiFetchSecretsInventoryRequest,
} from '../../../clients/cloud-vault-reporting/src';
import {
  formatDateTime,
  daysToGrpcDuration,
} from '../../../utils/date-time.ts';

export interface SecretInventorySignature {
  Args: {
    onFilterApplied: (value: Filter[]) => void;
    onSortApplied: (sort: string[]) => void;
    appliedFilters: Filter[];
    appliedSort: string[];
    onPageChange: (pagination: object) => void;
    pageSize: number;
    nextPageToken?: string;
    previousPageToken?: string;
    activeCluster: string;
  };

  Blocks: {
    default: [];
  };

  Element: HTMLElement;
}

export default class SecretInventory extends Component<SecretInventorySignature> {
  @service declare readonly reportingApi: ReportingApiService;

  @tracked page: Record<string, unknown>[] = [];
  @tracked isLoading = false;
  @tracked isError = false;
  @tracked nextPageToken?: string;
  @tracked previousPageToken?: string;
  @tracked pageSize: number = 10;
  @tracked totalUnfilteredCount: number = 0;

  handlePageSizeChange = (newPageSize: number) => {
    this.pageSize = newPageSize;
    this.nextPageToken = undefined;
    this.previousPageToken = undefined;
    this.args.onPageChange({
      page_size: newPageSize,
      next_page_token: undefined,
      previous_page_token: undefined,
    });
  };

  @tracked lastUpdatedTime: string = '';

  @tracked
  quickFilters: QuickFilter[] = [
    {
      label: 'Stale secrets',
      tooltip: 'Static secrets that have not been accessed in 90+ days.',
      getParams: () => {
        const filters: Filter[] = [
          {
            field: 'secretLastAccessedAt',
            operator: '<',
            value: {
              type: 'duration',
              value: daysToGrpcDuration(-90),
            },
          },
        ];
        return { filters };
      },
    },
    {
      label: 'Long lived secrets',
      tooltip:
        'Static, dynamic and rotating secrets that have not been updated in 90+ days.',
      getParams: () => {
        const filters: Filter[] = [
          {
            field: 'secretLastModifiedAt',
            operator: '<',
            value: {
              type: 'duration',
              value: daysToGrpcDuration(-90),
            },
          },
          {
            field: 'secretTypes',
            operator: '=',
            value: {
              type: 'list',
              value: ['static'],
            },
          },
        ];
        return { filters };
      },
    },
    {
      label: 'Upcoming secret rotations',
      tooltip:
        'Auto-rotating secrets which are scheduled to rotated in less than 30 days.',
      getParams: () => {
        const filters: Filter[] = [
          {
            field: 'nextRotationAt',
            operator: '<',
            value: {
              type: 'duration',
              value: daysToGrpcDuration(90),
            },
          },
          {
            field: 'secretTypes',
            operator: '=',
            value: {
              type: 'list',
              value: ['auto-rotating'],
            },
          },
        ];
        return { filters };
      },
    },
  ];
  filterFieldDefinitions: FilterFieldDefinition[] = [
    {
      label: 'Secret name',
      name: 'secretName',
      type: 'search',
    },
    {
      label: 'Secret engine',
      name: 'pluginName',
      type: 'single-select',
      options: [
        { name: 'KV-v1', value: 'kv-v1' },
        { name: 'KV-v2', value: 'kv-v2' },
        { name: 'AWS', value: 'aws' },
        { name: 'Database', value: 'database' },
      ],
    },
    {
      label: 'Type',
      name: 'secretTypes',
      type: 'multi-select',
      options: [
        { name: 'Static (KV)', value: 'static' },
        { name: 'Dynamic', value: 'dynamic' },
        { name: 'Auto-rotating', value: 'auto-rotating' },
      ],
    },
    {
      label: 'Namespace',
      name: 'namespace',
      type: 'text',
    },
    {
      label: 'Mount path',
      name: 'mountPath',
      type: 'text',
    },
    {
      label: 'Last accessed',
      name: 'secretLastAccessedAt',
      type: 'lookback',
      options: [
        { name: 'Last 7 days', value: daysToGrpcDuration(-7) },
        { name: 'Last 30 days', value: daysToGrpcDuration(-30) },
        { name: 'Last 90 days', value: daysToGrpcDuration(-90) },
      ],
    },
    {
      label: 'Last updated',
      name: 'secretLastModifiedAt',
      type: 'lookback',
      options: [
        { name: 'Last 7 days', value: daysToGrpcDuration(-7) },
        { name: 'Last 30 days', value: daysToGrpcDuration(-30) },
        { name: 'Last 90 days', value: daysToGrpcDuration(-90) },
      ],
    },
    {
      label: 'Next rotation',
      name: 'nextRotationAt',
      type: 'lookback',
      options: [
        { name: 'Next 7 days', value: daysToGrpcDuration(7) },
        { name: 'Next 30 days', value: daysToGrpcDuration(30) },
        { name: 'Next 90 days', value: daysToGrpcDuration(90) },
      ],
    },
  ];

  columns: InventoryTableColumn[] = [
    {
      label: 'Secret name',
      key: 'secretName',
      isSortable: true,
    },
    {
      label: 'Type',
      key: 'secretType',
      tooltip: 'Type of the secret, static, dynamic, or auto-rotating.',
    },
    {
      label: 'Namespace',
      key: 'namespace',
    },
    {
      label: 'Mount path',
      key: 'mountPath',
    },
    {
      label: 'Created by',
      key: 'createdById',
      tooltip: 'Entity ID of the user who created the secret.',
    },
    {
      label: 'Created on',
      key: 'createdAt',
      cellFormatter: (row: Record<string, unknown>) => {
        return formatDateTime(new Date(row['createdAt'] as string));
      },
    },
    {
      label: 'Last accessed',
      key: 'secretLastAccessedAt',
      isSortable: true,
      tooltip:
        'Last date and time that a secret was accessed by a user or machine.',
      cellFormatter: (row: Record<string, unknown>) => {
        return formatDateTime(new Date(row['secretLastAccessedAt'] as string));
      },
    },
  ];

  fetchQuickFilterCount = async (filter: QuickFilter) => {
    const params = filter.getParams();
    const { reporting } = this.reportingApi;
    const baseFilters: VaultReportingServiceApiFetchSecretsInventoryCountRequest =
      {
        ...flattenFilters(params.filters || []),
        organizationId: this.reportingApi.organizationId,
        projectId: this.reportingApi.projectId,
        clusterId: this.args.activeCluster,
      };
    const { count } = await reporting.fetchSecretsInventoryCount(baseFilters);
    const updatedFilter: QuickFilter = {
      ...filter,
      count: Number(count ?? 0),
    };

    return updatedFilter;
  };

  fetchQuickFilterCounts = async () => {
    const { count: totalUnfilteredCount } =
      await this.reportingApi.reporting.fetchSecretsInventoryCount({
        organizationId: this.reportingApi.organizationId,
        projectId: this.reportingApi.projectId,
        clusterId: this.args.activeCluster,
      });
    this.totalUnfilteredCount = totalUnfilteredCount
      ? Number(totalUnfilteredCount)
      : 0;
    this.quickFilters = await Promise.all(
      this.quickFilters.map((filter) => {
        return this.fetchQuickFilterCount(filter);
      }),
    );
  };

  handleDataUpdate = async (filters: Filter[]) => {
    const { reporting } = this.reportingApi;
    const flattenedFilters = flattenFilters(filters);

    const secretInventoryRequest: VaultReportingServiceApiFetchSecretsInventoryRequest =
      {
        ...flattenedFilters,
        organizationId: this.reportingApi.organizationId,
        projectId: this.reportingApi.projectId,
        clusterId: this.args.activeCluster,
        paginationPageSize: this.pageSize,
        paginationPreviousPageToken: this.args.previousPageToken,
        paginationNextPageToken: this.args.nextPageToken,
      };

    if (this.args.appliedSort && this.args.appliedSort.length > 0) {
      const [sortBy, sortOrder] = (this.args.appliedSort?.[0] || '').split(':');
      if (sortBy && sortOrder) {
        const sortByUnderscore = sortBy
          .replace(/([a-z])([A-Z])/g, '$1_$2')
          .toLowerCase();
        secretInventoryRequest.sortingOrderBy = [
          `${sortByUnderscore}:${sortOrder}`,
        ];
      }
    }
    this.isLoading = true;

    try {
      const { data, pagination, reportAsOf } =
        await reporting.fetchSecretsInventory(secretInventoryRequest);
      this.fetchQuickFilterCounts().catch((error) => {
        console.warn('Problem fetching quick filter counts:', error);
      });
      this.lastUpdatedTime = formatDateTime(reportAsOf || new Date());
      this.page = data as Record<string, unknown>[];
      this.nextPageToken = pagination?.nextPageToken;
      this.previousPageToken = pagination?.previousPageToken;
    } catch {
      this.isError = true;
    } finally {
      this.isLoading = false;
    }
  };

  <template>
    <div
      class="secret-inventory"
      data-test-vault-reporting-secret-inventory
      {{updateData this.handleDataUpdate @appliedFilters}}
    >
      <HdsPageHeader as |PH|>
        <PH.Title data-test-vault-reporting-secret-inventory-page-title>Secret
          Inventory
        </PH.Title>
        <PH.Description>
          <HdsTextBody
            @tag="p"
            @size="200"
            @color="primary"
            class="secret-inventory__description"
          >
            {{#if this.lastUpdatedTime}}

              Last updated
              {{this.lastUpdatedTime}}.
            {{/if}}
            Don’t see what you’re looking for?
            <HdsLinkInline
              class="dashboard__survey-link"
              data-test-vault-reporting-dashboard-survey-link
              @icon="external-link"
              @href="https://hashicorp.sjc1.qualtrics.com/jfe/form/SV_bqhLeB3deLd2caa"
              target="_blank"
            >Share feedback</HdsLinkInline>
          </HdsTextBody>

        </PH.Description>
        {{#if (has-block "actions")}}
          <PH.Actions>
            {{! @glint-expect-error  }}
            {{yield to="actions"}}
          </PH.Actions>
        {{/if}}
      </HdsPageHeader>
      <InventoryTable
        data-test-vault-reporting-secret-inventory-table
        @isLoading={{this.isLoading}}
        @isError={{this.isError}}
        @onFilterApplied={{@onFilterApplied}}
        @onSortApplied={{@onSortApplied}}
        @appliedFilters={{@appliedFilters}}
        @appliedSort={{@appliedSort}}
        @onPageChange={{@onPageChange}}
        @nextPageToken={{this.nextPageToken}}
        @previousPageToken={{this.previousPageToken}}
        @pageSize={{this.pageSize}}
        @onPageSizeChange={{this.handlePageSizeChange}}
        @filterFieldDefinitions={{this.filterFieldDefinitions}}
        @quickFilters={{this.quickFilters}}
        @columns={{this.columns}}
        @rows={{this.page}}
        @totalUnfilteredCount={{this.totalUnfilteredCount}}
      />
    </div>
  </template>
}
